import React, { useState, useEffect } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { checkAccess } from '../roles-helper'
import Spinner from './../spinner/spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLink } from '@fortawesome/free-solid-svg-icons'

import api from '../../api'

const Dashboard: React.FC = () => {
  const {
    selectedOrganization: [selectedOrganization],
  } = useOutletContext()
  const navigate = useNavigate()
  const [statisticsSubmitted, setStatisticsSubmitted] = useState(false)
  const [shouldNotSendStatistics, setShouldNotSendStatistics] = useState(false)
  const [agreementSubmitted, setAgreementSubmitted] = useState(false)
  const [paySubmitted, setPaySubmitted] = useState(false)
  const [ready, setReady] = useState(false)
  const [statisticsOpen, setStatisticsOpen] = useState(false)

  useEffect(() => {
    if (process.env.REACT_APP_SHOW_STATISTICS) {
      setStatisticsOpen(process.env.REACT_APP_SHOW_STATISTICS === 'true')
    }

    const fetchData = async () => {
      try {
        await getShouldSendStatistics()
        await getAgreementData()
        await getPayData()
        setReady(true)
      } catch (error) {
        console.error(error)
      }
    }

    if (selectedOrganization) {
      fetchData()
    }
  }, [selectedOrganization])

  const getShouldSendStatistics = async () => {
    try {
      const res = await api.get(`/accounts/${selectedOrganization.id}/statistics`)
      setStatisticsSubmitted(res.data.data.has_left_statsticas_year_c)
      setShouldNotSendStatistics(res.data.data.statistics === 'not_submit_statistics')
    } catch (e) {
      console.error(e)
    }
  }

  const getAgreementData = async () => {
    try {
      const res = await api.get(`/accounts/${selectedOrganization.id}/kpi`)
      const submittedhisYear = submittedThisYear(res.data.data)

      setAgreementSubmitted(submittedhisYear)
    } catch (e) {
      console.error(e)
    }
  }

  const getPayData = async () => {
    try {
      const res = await api.get(`/accounts/${selectedOrganization.id}/salaries`)
      const submittedhisYear = submittedThisYear(res.data.data)

      setPaySubmitted(submittedhisYear)
    } catch (e) {
      console.error(e)
    }
  }

  const submittedThisYear = (data) => {
    const currentYear = new Date().getFullYear() - 1
    return data.find((item: any) => item.year === currentYear) ? true : false
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col md:flex-row gap-12">
        {!ready && (
          <div className="flex initial flex-col justify-between items-center w-full gap-6 p-12 card">
            <Spinner />
          </div>
        )}
        {ready && checkAccess('PayData', selectedOrganization?.roles) && (
          <div className="flex initial flex-col justify-between items-start gap-6 p-12 card rounded-none w-full md:w-3/6 mb-12">
            <div className="flex flex-row justify-start items-start w-full gap-4 flex-wrap">
              <h2>Årslönesummor</h2>
              <div className={paySubmitted ? 'badge submitted' : 'badge not-submitted'}>
                {paySubmitted ? <span> Inlämnad</span> : <span> Ej inlämnad</span>}
              </div>
            </div>
            <p>Rapportering av slutliga löner efter årsslut.</p>
            <button
              onClick={() =>
                !paySubmitted ? navigate('/arslonesummor?step=1') : navigate('/arslonesummor')
              }
              className="btn"
            >
              {paySubmitted ? 'Se tidigare rapporter' : 'Fyll i årslönesummor'}
            </button>
          </div>
        )}
        {ready && checkAccess('AgreementPage', selectedOrganization?.roles) && (
          <div className="flex initial flex-col justify-between items-start gap-6 p-12 card w-full md:w-3/6 mb-12">
            <div className="flex flex-row justify-start items-start w-full gap-4 flex-wrap">
              <h2>Antal Anställda</h2>
              <div className={agreementSubmitted ? 'badge submitted' : 'badge not-submitted'}>
                {agreementSubmitted ? <span> Inlämnad</span> : <span> Ej inlämnad</span>}
              </div>
            </div>
            <p>Rapportering av antal anställda per avtalsområde datum 31 december.</p>
            <button
              onClick={() =>
                !agreementSubmitted
                  ? navigate('/antal-anstallda?step=1')
                  : navigate('/antal-anstallda')
              }
              className="btn"
            >
              {agreementSubmitted ? 'Se tidigare rapporter' : 'Fyll i antal anställda'}
            </button>
          </div>
        )}
      </div>
      {ready &&
        checkAccess('StatisticsPage', selectedOrganization?.roles) &&
        !shouldNotSendStatistics && (
          <div className="flex initial flex-col justify-between items-start gap-6 p-12 card ">
            <div className="flex flex-row justify-start items-start w-full gap-4 ">
              <h2>Lönestatistik 2026</h2>
              <div className={statisticsSubmitted ? 'badge submitted' : 'badge not-submitted'}>
                {statisticsSubmitted ? <span> Inlämnad</span> : <span> Ej inlämnad</span>}
              </div>
            </div>
            <p>
              Inlämning av lönestatistik sker under oktober månad och är obligatoriskt för alla
              medlemsföretag med fler än fem anställda.
            </p>
            <div className="flex flex-col">
              <button
                disabled={!statisticsOpen || statisticsSubmitted || shouldNotSendStatistics}
                onClick={() => navigate('/lonestatistik')}
                className={!statisticsSubmitted ? 'btn' : 'btn-disabled'}
              >
                {statisticsOpen ? 'Lämna in lönestatistik' : 'Insamling ej öppen'}
              </button>
            </div>
          </div>
        )}

      {ready &&
        (checkAccess('PayData', selectedOrganization?.roles) ||
          checkAccess('AgreementPage', selectedOrganization?.roles)) && (
          <div className="flex flex-col w-full items-start pt-12">
            <a
              className="text-black font-bold underline hover:font-bold text-lg mt-2"
              download
              target="_blank"
              rel="noopener noreferrer"
              href="https://youtu.be/f3OuM9KSC_o"
            >
              Instruktionsvideo för årslönerapportering <FontAwesomeIcon icon={faExternalLink} />
            </a>
          </div>
        )}
    </div>
  )
}

export default Dashboard
